import {
    FC,
    FormEvent,
    ReactElement,
    useMemo,
    useRef,
    useState,
} from 'react';

import {
    FormActionButtons,
    MonthYearInput,
    Notice,
    SearchableValueInput,
    TextInput,
} from '../../../compositions';
import { maxEmploymentYears } from '../../../constants';
import { startOfGivenMonth } from '../../../helpers/date';
import { scrollIntoView } from '../../../helpers/scroll';
import trans from '../../../helpers/trans';
import {
    EducationLevel,
    educationLevelOptions,
    educationLevelsWithFieldOfStudyOptions,
    EducationSubLevel,
    educationSubLevelOptions,
    LearningPath,
    learningPathOptions,
} from '../../../models/EducationLevels';
import { defaultSchoolExperience, SchoolExperience, SchoolExperienceFormData } from '../../../models/SchoolExperience/SchoolExperience';
import {
    DialogFormProps,
    FormOption,
    NoticeType,
    SearchableOption,
} from '../../../types';
import { EducationLevelSelector } from './subcomponents';
import { SchoolExperienceFormErrors, validateSchoolExperienceFormData } from './validations';

import './SchoolExperienceForm.scss';

interface SchoolExperienceFormProps extends DialogFormProps<SchoolExperienceFormData> {
    schoolExperience?: SchoolExperience;
    fieldOfStudyOptions?: FormOption[];
    onFieldOfStudyInput?: (value: string) => void;
    onSelectFieldOfStudy?: (value: string) => void;
    className?: string;
}

const SchoolExperienceForm: FC<SchoolExperienceFormProps> = ({
    isLoading,
    error,
    schoolExperience = defaultSchoolExperience(),
    fieldOfStudyOptions = [],
    onFieldOfStudyInput,
    onSelectFieldOfStudy,
    onSubmit,
    onCancel,
    className = '',
}): ReactElement => {
    const [formErrors, setFormErrors] = useState<SchoolExperienceFormErrors>({});

    const [educationLevel, setEducationLevel] = useState<EducationLevel>(schoolExperience.educationLevel);
    const [educationSubLevel, setEducationSubLevel] = useState<EducationSubLevel | undefined>(schoolExperience.educationSubLevel);
    const [learningPath, setLearningPath] = useState<LearningPath | undefined>(schoolExperience.learningPath);

    const [fieldOfStudy, setFieldOfStudy] = useState<string>(schoolExperience.fieldOfStudy);
    const [fieldOfStudyId, setFieldOfStudyId] = useState<string>(schoolExperience.fieldOfStudyId);

    const [school, setSchool] = useState<string>(schoolExperience.school);
    const [startDate, setStartDate] = useState<Date>(schoolExperience.startDate);
    const [endDate, setEndDate] = useState<Date>(schoolExperience.endDate);

    const formRef = useRef<HTMLFormElement>(null);

    const canSearchFieldsOfStudy = useMemo((): boolean => (
        educationLevelsWithFieldOfStudyOptions.includes(educationLevel)
    ), [educationLevel]);

    const handleEducationLevelChange = (value: EducationLevel): void => {
        setEducationLevel(value);
        setEducationSubLevel(undefined);
        setLearningPath(undefined);
    };

    const handleFieldOfStudyChange = (value: SearchableOption): void => {
        setFieldOfStudy(value.label);
        setFieldOfStudyId(value.value);

        if (onFieldOfStudyInput && !value.value && value.label) {
            onFieldOfStudyInput(value.label);
        }

        if (onSelectFieldOfStudy && value.value) {
            onSelectFieldOfStudy(value.value);
        }
    };

    const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
        event.preventDefault();

        const formData = {
            id: schoolExperience.id,
            educationLevel,
            educationSubLevel,
            learningPath,
            fieldOfStudy,
            fieldOfStudyId,
            school,
            startDate: startOfGivenMonth(startDate),
            endDate: startOfGivenMonth(endDate),
        };

        const [errors, hasErrors] = validateSchoolExperienceFormData(formData);

        setFormErrors(errors);

        if (!hasErrors) {
            onSubmit(formData);
        } else {
            scrollIntoView<HTMLFormElement>(formRef);
        }
    };

    return (
        <form
            ref={formRef}
            onSubmit={handleSubmit}
            className={`school-experience-form ${className}`}
        >
            <EducationLevelSelector
                educationLevel={educationLevel}
                educationSubLevel={educationSubLevel}
                learningPath={learningPath}
                educationLevelOptions={educationLevelOptions}
                educationSubLevelOptions={educationSubLevelOptions}
                learningPathOptions={learningPathOptions}
                formErrors={formErrors}
                onEducationLevelChange={handleEducationLevelChange}
                onEducationSubLevelChange={setEducationSubLevel}
                onLearningPathChange={setLearningPath}
            />

            {canSearchFieldsOfStudy ? (
                <div className="school-experience-form__input">
                    <SearchableValueInput
                        isSearchable
                        required
                        autoComplete="off"
                        name="fieldOfStudy"
                        label={trans('forms.schoolExperience.input.fieldOfStudy')}
                        placeholder={trans('forms.schoolExperience.placeholder.fieldOfStudy')}
                        value={fieldOfStudy}
                        options={fieldOfStudyOptions}
                        error={formErrors.fieldOfStudy}
                        onChange={handleFieldOfStudyChange}
                    />

                    <p className="school-experience-form__input-disclaimer">
                        {trans('forms.schoolExperience.disclaimer.fieldOfStudy')}
                    </p>
                </div>
            ) : (
                <TextInput
                    label={trans('forms.schoolExperience.input.fieldOfStudy')}
                    value={fieldOfStudy}
                    required
                    error={formErrors.fieldOfStudy}
                    onChange={setFieldOfStudy}
                    className="school-experience-form__input"
                />
            )}

            <TextInput
                label={trans('forms.schoolExperience.input.school')}
                value={school}
                error={formErrors.school}
                onChange={setSchool}
                className="school-experience-form__input"
            />

            <MonthYearInput
                label={trans('forms.schoolExperience.input.startDate')}
                name="startDate"
                required
                minYear={new Date().getFullYear() - maxEmploymentYears}
                maxYear={new Date().getFullYear()}
                value={startDate}
                error={formErrors.startDate}
                onChange={setStartDate}
                className="school-experience-form__input"
            />
            <MonthYearInput
                label={trans('forms.schoolExperience.input.endDate')}
                name="endDate"
                required
                minYear={startDate.getFullYear()}
                maxYear={startDate.getFullYear() + maxEmploymentYears}
                value={endDate}
                error={formErrors.endDate}
                onChange={setEndDate}
                className="school-experience-form__input"
            />

            {error && (
                <Notice
                    type={NoticeType.error}
                    text={error}
                    className="school-experience-form__error-message"
                />
            )}

            <FormActionButtons
                isLoading={isLoading}
                onCancelClick={onCancel}
                className="school-experience-form__action-buttons"
            />
        </form>
    );
};

export default SchoolExperienceForm;
